// export.ts — CSV export of the training log (all local; nothing leaves the device).
//
// Why this exists: the parity matrix claimed CSV export as a BETTER row and sold it against a
// competitor's Pro-gated export — but the feature did not exist. `BackupMenu` already renders an
// Export CSV button whenever `onExportCsv` is passed; App.tsx simply never passed it. Found by
// runtime verification 2026-08-30 (no export.ts, no CSV builder, page scan for "Export CSV" false).
//
// One row per SET, not per workout: a set is the unit a lifter actually reasons about, and a
// per-workout row would throw away the weight/reps that make the file worth opening.

import type { Workout, WorkoutSet } from "./store";

function cell(v: string | number | boolean | null | undefined): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function csv(headers: string[], rows: (string | number | boolean | null)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}

/** Full CSV: one row per set, with its workout's context carried on each row. */
export function workoutsCsv(items: { workout: Workout; sets: WorkoutSet[] }[]): string {
  const rows: (string | number | boolean | null)[][] = [];
  for (const { workout, sets } of items) {
    if (!sets.length) {
      // A logged workout with no sets is still a training day — keep it rather than
      // silently dropping it, or the file disagrees with the app's own session count.
      rows.push([workout.performed_on, workout.name, workout.duration_minutes, "", "", "", "", "", "", workout.notes ?? ""]);
      continue;
    }
    for (const s of sets) {
      rows.push([
        workout.performed_on,
        workout.name,
        workout.duration_minutes,
        s.exercise_name,
        s.set_index + 1,
        s.weight,
        s.reps,
        s.weight * s.reps,
        s.is_pr ? (s.pr_types ?? []).join(" · ") : "",
        s.notes ?? workout.notes ?? "",
      ]);
    }
  }
  // Sorted newest-first to match the app's own ordering — a file ordered differently from
  // the screen reads as a different dataset.
  rows.sort((a, b) => String(b[0]).localeCompare(String(a[0])));
  return csv(
    ["Date", "Workout", "Duration (min)", "Exercise", "Set", "Weight", "Reps", "Volume", "PR", "Notes"],
    rows,
  );
}

export function csvFilename(): string {
  const d = new Date();
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `workout-log-${iso}.csv`;
}
